import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { EnvService } from '@lib/env';
import {
    HttpExtras,
    IFileResponse,
    IPagination,
    IPaginationSortingResponse,
    NotificationService,
    SortDirection,
    Sorting,
} from '@lib/core';
import { Observable } from 'rxjs';
import { finalize, map, tap } from 'rxjs/operators';
import { UriBuilder } from 'uribuilder';
import {
    DeliveryReceipt,
    IDeliveryReceipt,
    IDeliveryReceiptViewResponse,
    DeliveryReceiptPossibleActionType,
} from './types/IDeliveryReceipt';
import { DeliveryReceiptFilter } from './types/DeliveryReceiptFilter';
import { DeliveryReceiptSearch } from './types/DeliveryReceiptSearch';
import { DeliveryReceiptSettings } from './types/DeliveryReceiptSettings';
import { DeliveryReceiptSorting } from './types/DeliveryReceiptSorting';
import { MatDialog } from '@angular/material/dialog';
import { BarcodeScanner } from '@awesome-cordova-plugins/barcode-scanner/ngx';
import { IDeliveryReceiptPermissionDto } from './types/IDeliveryReceiptPermissionDto';
import { Dayjs } from 'dayjs';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { FileSaverService } from 'ngx-filesaver';
import { I18nService } from '@lib/i18n';

export const DELIVERY_RECEIPT_SETTINGS_ROUTE_DATA_KEY = 'delivery-receipt-settings';

export interface ISearchDeliveryReceiptResultDto {
    id: number;
    receiptNumber: string;
    documentDate: string;
    supplierName: string;
    supplierNumber?: string | null;
    isOpen: boolean;
    inApproval: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class DeliveryReceiptDataService {
    private readonly notificationService = inject(NotificationService);
    private readonly env = inject(EnvService);
    private readonly http = inject(HttpClient);
    private readonly dialog = inject(MatDialog);
    private readonly barcodeScanner = inject(BarcodeScanner);
    private readonly fileSaverService = inject(FileSaverService);
    private readonly i18n = inject(I18nService);


    /**
     * Get all orders the user is allowed to see by filters, pagination and sorting.
     *
     * @param filter - filter object. If a field of this object is an empty string, no filter will be applied to the api call
     * @param pagination - pagination information. At least 'page' and 'elementsPerPage' need to be a valid integer
     * @param sorting - Sorting Object containing the column and direction. Both column and direction can be null or undefined.
     *                  If so, the default values OrderListSorting.ORDER_DATE as column and SortDirection.DESC as
     *                  direction will be applied as default.
     */
    public getAll(
        filter: DeliveryReceiptFilter,
        pagination: IPagination,
        sorting: Sorting<DeliveryReceiptSorting>,
    ): Observable<IPaginationSortingResponse<DeliveryReceiptSearch, DeliveryReceiptSorting>> {
        this.notificationService.spinner.show();

        const url: UriBuilder = this.getBase();

        url.query = {
            sortingColumn: sorting?.column ? sorting.column : DeliveryReceiptSorting.DELIVERY_DATE,
            sortingDirection: sorting?.direction ? sorting.direction : SortDirection.DESC,
            page: pagination.page - 1,
            elementsPerPage: pagination.elementsPerPage,
        };
        filter.apply(url);

        return this.http
            .get<IPaginationSortingResponse<DeliveryReceiptSearch, DeliveryReceiptSorting>>(url.toString(), {
                params: new HttpExtras({ ignoreCache: true }),
            })
            .pipe(
                map((data: IPaginationSortingResponse<DeliveryReceiptSearch, DeliveryReceiptSorting>) => {
                    data.pagination.page++;

                    return data;
                }),
                finalize(() => this.notificationService.spinner.hide()),
            );
    }

    public exportUnpaidDeliveries(deadline: Dayjs): Observable<HttpResponse<Blob>> {
        const url: UriBuilder = this.env.getBase('invoice', 'export', 'unpaid');
        url.query['deadline'] = deadline.endOf('day').format('YYYY-MM-DD');
        return this.http.get(url.toString(), { responseType: 'blob', observe: 'response' }).pipe(
            tap(async (res: HttpResponse<Blob>) => {
                const fileName = `${await this.i18n.translateSync(marker('Offene Posten bis'))} ${deadline.format(
                    'YYYY-MM-DD',
                )}.xlsx`;
                this.fileSaverService.save(res.body, fileName);
            }),
        );
    }

    public getSettings(): Observable<DeliveryReceiptSettings> {
        return this.http.get<DeliveryReceiptSettings>(this.getBase('setting').toString());
    }

    public hasCreatePermissionFor(orderId: string): Observable<boolean> {
        const uri = this.getBase('has-create-permission');
        uri.query['orderId'] = orderId;

        return this.http
            .get<IDeliveryReceiptPermissionDto>(uri.toString())
            .pipe(map((permission) => permission.hasPermission));
    }

    public hasViewPermission(receiptId: string): Observable<boolean> {
        const uri = this.getBase('has-view-permission');
        uri.query['receiptId'] = receiptId;

        return this.http
            .get<IDeliveryReceiptPermissionDto>(uri.toString())
            .pipe(map((permission) => permission.hasPermission));
    }

    /**
     * Whether the current user may set the final-delivery flag (Endlieferkennzeichen) while booking
     * a goods receipt — the visibility source for the checkbox in the entry dialog (EPROC-11643).
     * Answers the same ACL rule as the server-side gate on POST /delivery-receipt, so the dialog
     * never offers a checkbox whose payload would be rejected. Asked once per dialog, not per
     * position: the right is the user's, not the order item's.
     */
    public hasDeliveryCompletedPermission(): Observable<boolean> {
        return this.http
            .get<IDeliveryReceiptPermissionDto>(this.getBase('has-delivery-completed-permission').toString())
            .pipe(map((permission) => permission.hasPermission));
    }

    /**
     * GET delivery receipts by orderId
     * @param orderNumber - orderNumber to get the linked delivery receipt
     */
    public getByOrderNumber(orderNumber: string): Observable<DeliveryReceipt[]> {
        const url: UriBuilder = this.getBase();
        url.pathSegments = [...url.pathSegments, 'order'];
        url.query['orderNumber'] = orderNumber;

        return this.http
            .get<IDeliveryReceipt[]>(url.toString(), {
                params: new HttpExtras({ ignoreCache: true }),
            })
            .pipe(
                map((receipts: IDeliveryReceipt[]) =>
                    receipts.map((receipt) => new DeliveryReceipt(receipt, this.dialog, this.barcodeScanner)),
                ),
            );
    }

    /**
     * Get delivery receipt by id
     * @param id - delivery receipt id
     */
    public getById(
        id: number,
    ): Observable<{ data: DeliveryReceipt; possibleActions: DeliveryReceiptPossibleActionType[] }> {
        const url = this.env.getBase('delivery-receipt', 'v2', id);

        return this.http
            .get<IDeliveryReceiptViewResponse>(url.toString(), { params: new HttpExtras({ ignoreCache: true }) })
            .pipe(
                map((response) => ({
                    data: new DeliveryReceipt(response.data, this.dialog, this.barcodeScanner),
                    possibleActions: response.possibleActions,
                })),
            );
    }

    /**
     * Fetch receipts that are related to the receipt passed as parameter (baseReceiptId). Two receipts are related if they include
     * items from the same order
     * @param baseReceiptId -
     */
    public getRelatedReceipts(baseReceiptId: number): Observable<DeliveryReceiptSearch[]> {
        const url = this.env.getBase('delivery-receipt', baseReceiptId, 'related');

        return this.http.get<DeliveryReceiptSearch[]>(url.toString());
    }

    /**
     * Search delivery receipts by supplier and optional search query. If searchQuery is empty, returns last 50.
     * Matches backend endpoint GET /delivery-receipt/search?supplierCompanyId=&searchQuery=&includeClosed=
     */
    public searchBySupplier(
        supplierCompanyId: string,
        searchQuery = '',
        includeClosed: boolean,
    ): Observable<ISearchDeliveryReceiptResultDto[]> {
        const base = UriBuilder.parse(this.env.apiUrl);
        base.pathSegments = [...base.pathSegments, 'delivery-receipt', 'search'];
        base.query['supplierCompanyId'] = supplierCompanyId;
        base.query['includeClosed'] = includeClosed.toString();
        if (searchQuery) {
            base.query['searchQuery'] = searchQuery;
        }
        return this.http.get<ISearchDeliveryReceiptResultDto[]>(base.toString(), {
            params: new HttpExtras({ ignoreCache: true }),
        });
    }

    /**
     * Upload a file and attach it to a receipt
     * @param receiptId - the receipt id
     * @param formData - formData with the file
     * @param customErrorhandling - error and success notifications will be custom handled
     */
    public upload(receiptId: number, formData: FormData, customErrorhandling = false): Observable<IFileResponse[]> {
        const url: UriBuilder = this.env.getBase('delivery-receipt', receiptId, 'upload');
        this.notificationService.spinner.show();

        return this.http
            .post<IFileResponse[]>(url.toString(), formData, { params: new HttpExtras({ customErrorhandling }) })
            .pipe(finalize(() => this.notificationService.spinner.hide()));
    }

    /**
     * Upload a file and attach it to a receipt
     * @param receiptId - the receipt id
     * @param formData - formData with the file
     * @param customErrorhandling - error and success notifications will be custom handled
     */
    public uploadScannedImages(
        receiptId: string | number,
        formData: FormData,
        customErrorhandling = false,
    ): Observable<IFileResponse> {
        const url: UriBuilder = this.env.getBase('delivery-receipt', receiptId, 'attach-images-as-pdf');
        this.notificationService.spinner.show();

        return this.http
            .post<IFileResponse>(url.toString(), formData, { params: new HttpExtras({ customErrorhandling }) })
            .pipe(finalize(() => this.notificationService.spinner.hide()));
    }

    /**
     * Delete a file that is attached to a receipt
     * @param receiptId - the receipt id
     * @param fileId - the id of the file that will be deleted
     */
    public deleteFile(receiptId: number, fileId: string): Observable<IFileResponse[]> {
        const url: UriBuilder = this.getBase();
        url.pathSegments = [...url.pathSegments, receiptId.toString(), 'file', fileId];

        return this.http.delete<IFileResponse[]>(url.toString());
    }

    public saveDeliveryReceiptComment(receiptId: number, comment: string): Observable<void> {
        const uri = this.env.getBase('delivery-receipt', receiptId, 'comment');

        return this.http.put<void>(uri.toString(), { comment });
    }

    /**
     * Returns base delivery-receipt api endpoint as UriBuilder instance
     */
    private getBase(...segements: string[]): UriBuilder {
        const url: UriBuilder = UriBuilder.parse(this.env.apiUrl);
        url.pathSegments = [
            ...url.pathSegments,
            'delivery-receipt',
            ...(segements && segements.length > 0 ? segements : []),
        ];

        return url;
    }
}
